import React, { useState, useEffect } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, DatePicker, message, Upload } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, FilePdfOutlined, FileExcelOutlined, PrinterOutlined } from "@ant-design/icons";
import api, { API_BASE_URL } from "../api";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const Visitor = () => {
    const [data, setData] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const res = await api.get("/visitor/get-all-visitors");
            if (res.data && res.data.success) {
                setData(res.data.data);
                setFilteredData(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching visitors:", err);
            message.error("Failed to fetch visitors.");
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        if (!value.trim()) {
            setFilteredData(data);
            return;
        }
        const search = value.toLowerCase().trim();
        const filtered = data.filter(item =>
            (item.name && item.name.toLowerCase().includes(search)) ||
            (item.phone && item.phone.includes(search)) ||
            (item.altPhone && item.altPhone.includes(search)) ||
            (item.email && item.email.toLowerCase().includes(search)) ||
            (item.companyName && item.companyName.toLowerCase().includes(search)) ||
            (item.businessCategory && item.businessCategory.toLowerCase().includes(search)) ||
            (item.referredBy && item.referredBy.toLowerCase().includes(search)) ||
            (item.nativity && item.nativity.toLowerCase().includes(search)) ||
            (item.kuladaivam && item.kuladaivam.toLowerCase().includes(search))
        );
        setFilteredData(filtered);
    };

    const handleAdd = () => {
        setEditMode(false);
        setCurrentRecord(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditMode(true);
        setCurrentRecord(record);

        const formValues = {
            ...record,
            eventDate: record.eventDate ? moment(record.eventDate) : null,
            dob: record.dob ? moment(record.dob) : null,
            anniversary: record.anniversary ? moment(record.anniversary) : null,
            dobAttachment: record.dobAttachment ? [{ uid: '-1', name: 'dob_proof.jpg', status: 'done', url: `${API_BASE_URL}/${record.dobAttachment}` }] : [],
            regFormAttachment: record.regFormAttachment ? [{ uid: '-2', name: 'registration_form.jpg', status: 'done', url: `${API_BASE_URL}/${record.regFormAttachment}` }] : [],
            certAttachment: record.certAttachment ? [{ uid: '-3', name: 'certificate.jpg', status: 'done', url: `${API_BASE_URL}/${record.certAttachment}` }] : [],
        };

        form.setFieldsValue(formValues);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Are you sure you want to delete this visitor?",
            onOk: async () => {
                try {
                    await api.delete(`/visitor/deleteVisitor/${id}`);
                    message.success("Visitor deleted successfully");
                    fetchData();
                } catch (err) {
                    message.error("Failed to delete visitor.");
                }
            },
        });
    };

    const handleOk = async () => {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const formData = new FormData();

            formData.append("name", values.name);
            formData.append("phone", values.phone);
            formData.append("altPhone", values.altPhone || "");
            formData.append("email", values.email || "");
            formData.append("companyName", values.companyName || "");
            formData.append("businessCategory", values.businessCategory || "");
            formData.append("referredBy", values.referredBy || "");
            formData.append("eventDescription", values.eventDescription || "");
            formData.append("address", values.address || "");
            formData.append("nativity", values.nativity || "");
            formData.append("kuladaivam", values.kuladaivam || "");
            formData.append("amount", values.amount || 0);

            if (values.eventDate) {
                formData.append("eventDate", values.eventDate.format("YYYY-MM-DD"));
            }
            if (values.dob) {
                formData.append("dob", values.dob.format("YYYY-MM-DD"));
            }
            if (values.anniversary) {
                formData.append("anniversary", values.anniversary.format("YYYY-MM-DD"));
            }

            if (values.dobAttachment && values.dobAttachment[0] && values.dobAttachment[0].originFileObj) {
                formData.append("dobAttachment", values.dobAttachment[0].originFileObj);
            }
            if (values.regFormAttachment && values.regFormAttachment[0] && values.regFormAttachment[0].originFileObj) {
                formData.append("regFormAttachment", values.regFormAttachment[0].originFileObj);
            }
            if (values.certAttachment && values.certAttachment[0] && values.certAttachment[0].originFileObj) {
                formData.append("certAttachment", values.certAttachment[0].originFileObj);
            }

            if (editMode) {
                await api.put(`/visitor/updateVisitor/${currentRecord._id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                message.success("Visitor updated successfully");
            } else {
                await api.post("/visitor/addVisitor", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                message.success("Visitor added successfully");
            }
            setIsModalVisible(false);
            fetchData();
        } catch (err) {
            console.error("Form validation failed:", err);
            message.error("Please fill in all required fields.");
        } finally {
            setLoading(false);
        }
    };

    const getBase64Image = async (url) => {
        try {
            const response = await fetch(url, { mode: "cors" });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = () => reject(new Error("Failed to convert image to Base64"));
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error(`Failed to fetch image from ${url}:`, error);
            return null;
        }
    };

    const numberToWords = (num) => {
        const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
        const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
        const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
        const units = ["", "Thousand", "Million", "Billion"];

        if (num === 0) return "Zero";

        const convertLessThanThousand = (n) => {
            if (n === 0) return "";
            if (n < 10) return ones[n];
            if (n < 20) return teens[n - 10];
            if (n < 100) return `${tens[Math.floor(n / 10)]} ${ones[n % 10]}`.trim();
            return `${ones[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim();
        };

        const integerPart = Math.floor(num);
        let words = "";
        let n = integerPart;

        if (n === 0) words = "Zero";
        else {
            let i = 0;
            while (n > 0) {
                if (n % 1000 !== 0) {
                    words = `${convertLessThanThousand(n % 1000)} ${units[i]} ${words}`.trim();
                }
                n = Math.floor(n / 1000);
                i++;
            }
        }

        return words;
    };

    const printPDF = async (record) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        const acciLogoUrl = "/logo.png";
        const acciLogo = await getBase64Image(acciLogoUrl);
        if (acciLogo) {
            const imgWidth = 25;
            const imgHeight = 25;
            doc.addImage(acciLogo, "PNG", 20, 10, imgWidth, imgHeight);
        }

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("AGAMUDAYAR", pageWidth / 2, 18, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("CHAMBER OF COMMERCE AND INDUSTRIES", pageWidth / 2, 24, { align: "center" });

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Visitor Pass / Receipt", pageWidth / 2, 38, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 48);

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("Visitor Details:", 20, 58);

        const tableColumn = ["Field", "Detail"];
        const tableRows = [
            ["Name", record.name || ""],
            ["Mobile No", record.phone || ""],
            ["Alternative Mobile No", record.altPhone || ""],
            ["Email", record.email || ""],
            ["Company Name", record.companyName || ""],
            ["Business Category", record.businessCategory || ""],
            ["Referred By", record.referredBy || ""],
            ["Event Description", record.eventDescription || ""],
            ["Event Date", record.eventDate ? moment(record.eventDate).format("YYYY-MM-DD") : ""],
            ["Address", record.address || ""],
            ["Nativity", record.nativity || ""],
            ["Kuladaivam", record.kuladaivam || ""],
            ["Date Of Birth", record.dob ? moment(record.dob).format("YYYY-MM-DD") : ""],
            ["Anniversary", record.anniversary ? moment(record.anniversary).format("YYYY-MM-DD") : ""],
            ["Visitor Fee", `Rs. ${(parseFloat(record.amount) || 0).toFixed(2)}`],
        ];

        doc.autoTable({
            startY: 64,
            head: [tableColumn],
            body: tableRows,
            theme: "grid",
            styles: { fontSize: 9 },
            headStyles: { fillColor: [0, 128, 128], textColor: 255 },
            margin: { left: 20, right: 20 },
        });

        const finalY = doc.lastAutoTable.finalY || 160;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`In words: ${numberToWords(parseFloat(record.amount || 0))} Only`, 20, finalY + 10);

        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("Thank you for visiting!", pageWidth / 2, pageHeight - 30, { align: "center" });

        doc.setFontSize(8);
        doc.text("Administrative Office, Viswanathapuram, Madurai - 625 014", pageWidth / 2, pageHeight - 20, { align: "center" });

        const pdfBlob = doc.output("blob");
        const pdfURL = URL.createObjectURL(pdfBlob);
        window.open(pdfURL, "_blank");
    };

    const printPDFA5 = async (record) => {
        const margin = 5;
        const pageWidth = 80; // 80mm roll size for receipt printer fallback
        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: [pageWidth, 130],
        });

        let currentY = 8;

        // Logo
        const acciLogoUrl = "/logo.png";
        const acciLogo = await getBase64Image(acciLogoUrl);
        if (acciLogo) {
            doc.addImage(acciLogo, "PNG", (pageWidth - 12) / 2, currentY, 12, 12);
            currentY += 15;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("AGAMUDAYAR ACCI", pageWidth / 2, currentY, { align: "center" });
        currentY += 4;

        doc.setFontSize(8);
        doc.text("VISITOR RECEIPT", pageWidth / 2, currentY, { align: "center" });
        currentY += 5;

        doc.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 4;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, currentY);
        currentY += 4;

        doc.setFont("helvetica", "bold");
        doc.text(`Name: ${record.name || ""}`, margin, currentY);
        currentY += 4;
        doc.text(`Phone: ${record.phone || ""}`, margin, currentY);
        currentY += 4;
        doc.text(`Company: ${record.companyName || ""}`, margin, currentY);
        currentY += 5;

        doc.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 4;

        doc.text(`Visitor Fee: Rs. ${(parseFloat(record.amount) || 0).toFixed(2)}`, margin, currentY);
        currentY += 4;

        const amountWords = `In Words: ${numberToWords(parseFloat(record.amount || 0))} Only`;
        const splitWords = doc.splitTextToSize(amountWords, pageWidth - (margin * 2));
        doc.text(splitWords, margin, currentY);
        currentY += (splitWords.length * 3.5) + 5;

        doc.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 5;

        doc.setFont("helvetica", "bold");
        doc.text("Thank you for your visit!", pageWidth / 2, currentY, { align: "center" });

        const pdfBlob = doc.output("blob");
        const pdfURL = URL.createObjectURL(pdfBlob);
        window.open(pdfURL, "_blank");
    };

    const exportExcel = () => {
        const excelData = filteredData.map((record, index) => ({
            "S.No": index + 1,
            "Name": record.name || "",
            "Mobile No": record.phone || "",
            "Alternative Mobile No": record.altPhone || "",
            "Email": record.email || "",
            "Company Name": record.companyName || "",
            "Business Category": record.businessCategory || "",
            "Referred By": record.referredBy || "",
            "Event Description": record.eventDescription || "",
            "Event Date": record.eventDate ? moment(record.eventDate).format("YYYY-MM-DD") : "",
            "Address": record.address || "",
            "Nativity": record.nativity || "",
            "Kuladaivam": record.kuladaivam || "",
            "Date Of Birth": record.dob ? moment(record.dob).format("YYYY-MM-DD") : "",
            "Anniversary": record.anniversary ? moment(record.anniversary).format("YYYY-MM-DD") : "",
            "Visitor Fee": record.amount || 0,
        }));

        const totalRow = {
            "S.No": "",
            "Name": "Total Visitors",
            "Mobile No": excelData.length,
            "Alternative Mobile No": "",
            "Email": "",
            "Company Name": "",
            "Business Category": "",
            "Referred By": "",
            "Event Description": "",
            "Event Date": "",
            "Address": "",
            "Nativity": "",
            "Kuladaivam": "",
            "Date Of Birth": "",
            "Anniversary": "",
            "Visitor Fee": excelData.reduce((sum, r) => sum + (parseFloat(r["Visitor Fee"]) || 0), 0),
        };

        const worksheet = XLSX.utils.json_to_sheet([...excelData, totalRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Visitors");
        XLSX.writeFile(workbook, "Visitors_List.xlsx");
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        doc.text("Visitor Register Report", 20, 10);
        const tableColumn = [
            "S.No",
            "Name",
            "Mobile No",
            "Email",
            "Company Name",
            "Business Category",
            "Referred By",
            "Visitor Fee",
        ];

        const tableRows = filteredData.map((record, index) => [
            index + 1,
            record.name || "",
            record.phone || "",
            record.email || "",
            record.companyName || "",
            record.businessCategory || "",
            record.referredBy || "",
            (parseFloat(record.amount) || 0).toFixed(2),
        ]);

        doc.autoTable({ head: [tableColumn], body: tableRows });
        doc.save("visitors_report.pdf");
    };

    const columns = [
        { title: "S.No", render: (_, __, index) => index + 1 },
        { title: "Name", dataIndex: "name", key: "name", sorter: (a, b) => (a.name || "").localeCompare(b.name || "") },
        { title: "Mobile No", dataIndex: "phone", key: "phone" },
        { title: "Email", dataIndex: "email", key: "email" },
        { title: "Company Name", dataIndex: "companyName", key: "companyName" },
        { title: "Business Category", dataIndex: "businessCategory", key: "businessCategory" },
        { title: "Referred By", dataIndex: "referredBy", key: "referredBy" },
        { title: "Visitor Fee", dataIndex: "amount", key: "amount", render: (value) => `Rs. ${(parseFloat(value) || 0).toFixed(2)}` },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit" />
                    <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} title="Delete" />
                    <Button icon={<PrinterOutlined />} onClick={() => printPDF(record)} title="Print A4" style={{ color: "teal" }} />
                    <Button icon={<PrinterOutlined />} onClick={() => printPDFA5(record)} title="Print A5" style={{ color: "green" }} />
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <h2 style={{ color: "teal", marginBottom: "20px" }}>Visitor List</h2>
            <Row gutter={16} style={{ marginBottom: "20px" }}>
                <Col span={12}>
                    <Input.Search
                        placeholder="Search by name, phone, email, company, category, etc."
                        allowClear
                        value={searchText}
                        onChange={(e) => handleSearch(e.target.value)}
                        onSearch={handleSearch}
                    />
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ backgroundColor: "teal" }}>
                        Add Visitor
                    </Button>
                    <Button icon={<FileExcelOutlined />} onClick={exportExcel} style={{ marginLeft: 8, color: "green" }}>
                        Export Excel
                    </Button>
                    <Button icon={<FilePdfOutlined />} onClick={exportPDF} style={{ marginLeft: 8, color: "red" }}>
                        Export PDF
                    </Button>
                </Col>
            </Row>
            <Table columns={columns} dataSource={filteredData} rowKey="_id" />

            <Modal
                title={editMode ? "Edit Visitor" : "Add Visitor"}
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={() => setIsModalVisible(false)}
                okButtonProps={{ loading }}
                width={900}
            >
                <Form form={form} layout="vertical">
                    {/* Row 1 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter name" }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="phone" label="Mobile No" rules={[{ required: true, message: "Please enter mobile number" }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="altPhone" label="Alternative Mobile No">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 2 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="email" label="Email">
                                <Input type="email" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="companyName" label="Company Name">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="businessCategory" label="Business Category">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 3 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="referredBy" label="Reff:By">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="eventDescription" label="Event Description">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="eventDate" label="Event Date">
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 4 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="address" label="Address">
                                <Input.TextArea rows={1} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="nativity" label="Nativity">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="kuladaivam" label="Kuladaivam">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 5 */}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="dob" label="Date Of Birth">
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="anniversary" label="Anniversary">
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 6 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="dobAttachment"
                                label="Date Of Birth"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                            >
                                <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                                    <div>
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </div>
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="regFormAttachment"
                                label="Registration Form"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                            >
                                <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                                    <div>
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </div>
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="certAttachment"
                                label="Certificate"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                            >
                                <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                                    <div>
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </div>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 7 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="amount" label="Visitor Fee">
                                <Input type="number" step="0.01" min="0" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default Visitor;
